import styled, { css } from 'styled-components'
import { theme } from '~/styles'

interface IMultipleTags {
  hasMultipleTags?: boolean
  maxWidth?: string
}

interface SelectWrapperStyles {
  flex?: string
  disabled?: boolean
  disabledBackground?: string
}

export const SelectWrapper = styled.div<SelectWrapperStyles>`
  min-height: 40px;
  line-height: 40px;
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  color: ${theme.colors.color6b7786};
  font-size: 14px;
  flex: ${props => (!props.flex ? '1' : props.flex)};

  ${props =>
    props.disabled &&
    css`
      input,
      div {
        background: ${props.disabledBackground ?? props.theme.colors.colore9ecf3};
        pointer-events: none;
        border-radius: ${({ theme }) => theme.zds.border.radius100};
      }
    `}
`

interface ISelectGroupProps extends IMultipleTags {}
export const SelectGroup = styled.div<ISelectGroupProps>`
  width: 100%;
  position: relative;
  ${({ hasMultipleTags }) =>
    hasMultipleTags &&
    css`
      height: 40px;
    `}
`

export const CountResults = styled.div`
  text-align: left;
  font-size: 15px;
  line-height: 20px;
  font-family: 'Inter', sans-serif;
  color: ${props => props.theme.colors.color5a6a7e};
  padding: ${({ theme }) => theme.zds.spacing.insetEqual500};
`

export const CountResultsText = styled.div`
  font-weight: bold;
`

interface IInputWrapper extends IMultipleTags {
  error: boolean
}
export const InputWrapper = styled.div<IInputWrapper>`
  border-radius: ${({ theme }) => theme.zds.border.radius100};
  border: ${({ error }) => (error ? theme.colors.colorFF2424 : theme.colors.colore9ecf3)} 1px solid;
  background-color: ${({ theme }) => theme.zds.colors.neutral1000};
  display: flex;
  width: 100%;
  ${({ error }) => (error ? `box-shadow: 0px 0px 6px 0px ${theme.colors.colorFF2424}4D` : null)};
  cursor: pointer;
  ${({ maxWidth }) =>
    maxWidth &&
    css`
      max-width: ${maxWidth} !important;
    `};
  ${({ hasMultipleTags }) =>
    hasMultipleTags &&
    css`
      min-width: 100%;
    `}
`

export const InputContainer = styled.div`
  display: flex;
  flex: 1;
  width: auto;
`
interface IInput extends IMultipleTags {
  textAlign?: 'left' | 'center' | 'end'
}
export const Input = styled.input<IInput>`
  outline: none;
  width: 100%;
  height: 38px;
  padding: ${props => (props.textAlign !== 'center' ? '8px' : '0px')};
  border: none;
  color: ${props => props.theme.colors.color2d3139};
  text-align: ${props => (props.textAlign ? props.textAlign : 'center')};
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  cursor: ${props => {
    return props.readOnly ? 'default' : 'text'
  }};
  ::placeholder {
    color: ${theme.colors.color6b7786};
    font-size: 14px;
    opacity: 1;
  }
  ${({ hasMultipleTags }) =>
    hasMultipleTags &&
    css`
      min-width: 30px;
    `}
`
export const ErrorWrapper = styled.div`
  text-align: left;
  position: absolute;
  bottom: -25px;
  margin: ${({ theme }) => theme.zds.spacing.stack300} 0px;
  font-size: 12px;
  min-height: 12px;
  line-height: 12px;
  color: ${theme.colors.colorFF2424};
`

interface IOptionsWrapperProps extends IMultipleTags {
  isOptionsOpen: boolean
  maxHeight: string
  showOptionsAbove: boolean
}
export const OptionsWrapper = styled.div<IOptionsWrapperProps>`
  position: absolute;
  z-index: 10;
  width: 100%;
  border: ${theme.colors.colore9ecf3} 1px solid;
  background-color: ${({ theme }) => theme.zds.colors.neutral1000};
  overflow: auto;
  ${({ maxHeight }) => css`
    max-height: ${maxHeight} !important;
  `};
  display: ${({ isOptionsOpen }) => (isOptionsOpen ? '' : 'none')};
  visibility: ${({ isOptionsOpen }) => (isOptionsOpen ? 'visible' : 'hidden')};
  ${({ hasMultipleTags, showOptionsAbove }) => {
    if (!hasMultipleTags && showOptionsAbove) {
      return css`
        bottom: 37px;
        border-top-left-radius: ${({ theme }) => theme.zds.border.radius100};
        border-top-right-radius: ${({ theme }) => theme.zds.border.radius100};
        box-shadow: 3px -10px 10px -2px ${theme.colors.colordfe3e8};
      `
    }
    if (!hasMultipleTags && !showOptionsAbove) {
      return css`
        top: 37px;
        border-bottom-left-radius: ${({ theme }) => theme.zds.border.radius100};
        border-bottom-right-radius: ${({ theme }) => theme.zds.border.radius100};
        box-shadow: 3px 10px 10px -2px ${theme.colors.colordfe3e8};
      `
    }
  }}
`

export interface IOptionColor {
  backgroundColorOnHover?: string
  colorOnHover?: string
  selectedBackgroundColor?: string
  selectedColor?: string
}

interface IOption extends IOptionColor {
  selected?: boolean
  optionWithKeyDown: boolean
}

export const Option = styled.div<IOption>`
  padding: 0px ${({ theme }) => theme.zds.spacing.insetEqual500};
  cursor: pointer;
  background-color: ${({ optionWithKeyDown, backgroundColorOnHover }) =>
    optionWithKeyDown && `${backgroundColorOnHover ?? theme.colors.colore9ecf3}`};
  transition: 200ms all;
  &:hover {
    background-color: ${({ backgroundColorOnHover }) => `${backgroundColorOnHover ?? theme.colors.colordfe3e8}`};
    color: ${({ colorOnHover }) => `${colorOnHover ?? theme.colors.color6b7786}`};
  }
  ${({ selected, selectedBackgroundColor, optionWithKeyDown, selectedColor }) =>
    !optionWithKeyDown &&
    selected &&
    css`
      background-color: ${selectedBackgroundColor ?? theme.colors.colorBCC9D9};
      color: ${selectedColor ?? theme.colors.color6b7786};
    `}
`

export const NoOptionFound = styled.div`
  padding: 0px ${({ theme }) => theme.zds.spacing.insetEqual500};
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
`
export const LoadingContainer = styled.div`
  padding: 50px ${({ theme }) => theme.zds.spacing.insetEqual500};
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
`

interface IIconWrapper {
  rotate: string
  error: boolean
  iconLeft?: boolean
}
export const IconWrapper = styled.div<IIconWrapper>`
  display: flex;
  margin-right: ${({ theme }) => theme.zds.spacing.stack400};
  flex-grow: 1;
  align-items: center;
  transform: rotate(0deg);
  transition: all 0.3s ease-out;
  transform-origin: top 0 bottom;
  transform: ${({ rotate }) => (rotate === 'true' ? 'rotate3d(1, 0, 0, -0.5turn)' : '')};
  color: ${({ error }) => (error ? theme.colors.colorFF2424 : theme.colors.colora8abb1)};
  fill: ${({ error }) => (error ? theme.colors.colorFF2424 : theme.colors.colora8abb1)};
  cursor: pointer;
  ${({ iconLeft, theme }) =>
    iconLeft &&
    css`
      margin-left: ${theme.zds.spacing.stack400};
      margin-right: 0;
    `}
`
export const ErrorIconWrapper = styled.div`
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 16px;
  color: ${theme.colors.colorFF2424};
`
interface ITagsContainerProps extends IMultipleTags {}
export const TagsContainer = styled.div<ITagsContainerProps>`
  width: 100%;
  ${({ hasMultipleTags, theme }) =>
    hasMultipleTags &&
    css`
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      span {
        margin: ${theme.zds.spacing.stack300} ${theme.zds.spacing.inline200};
      }
    `}
`
